import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PlusIcon, XMarkIcon, UserIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

interface PolicyActivityLog {
  id: number;
  policy_id: number;
  action_type: string;
  description: string;
  created_by: string;
  created_at: string;
}

interface PolicyNotesTabProps {
  policyId: number;
  onActivityChange?: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const PolicyNotesTab: React.FC<PolicyNotesTabProps> = ({ policyId, onActivityChange }) => {
  const [activityLogs, setActivityLogs] = useState<PolicyActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchActivityLogs = useCallback(async () => {
    if (!policyId || isNaN(policyId)) return;

    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/policy-notes/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const payload = response.data;
      const list: PolicyActivityLog[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setActivityLogs(list);
    } catch (err: any) {
      console.error('❌ Error fetching policy activity logs:', err);
      setError(err.response?.data?.message || 'Failed to load activity logs');
    } finally {
      setIsLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  useEffect(() => {
    const handleRefresh = () => fetchActivityLogs();
    window.addEventListener('refresh-policy-notes', handleRefresh);
    return () => window.removeEventListener('refresh-policy-notes', handleRefresh);
  }, [fetchActivityLogs]);

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setError('Please enter a note');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

            const performedBy = (() => {
        try {
          const stored = localStorage.getItem('user');
          if (!stored) return 'System';
          const u = JSON.parse(stored);
          const name = u.username || u.email || 'System';
          const surname = u.surname || '';
          return `${name} ${surname}`.trim() || name;
        } catch {
          return 'System';
        }
      })();

      await axios.post(
        `${API_URL}/api/policy-notes`,
        {
          policy_id: policyId,
          action_type: 'NOTE_ADDED',
          description: newNote.trim(),
          created_by: performedBy,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Note added successfully');
      setNewNote('');
      setShowAddNote(false);

      await fetchActivityLogs();
      onActivityChange?.();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('❌ Error adding policy note:', err);
      setError(err.response?.data?.message || 'Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = (dateString: string): string =>
    new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getActionIcon = (action: string): string => {
    switch (action) {
      case 'NOTE_ADDED':        return '📝';
      case 'CREATED':           return '✨';
      case 'EDITED':            return '✏️';
      case 'CANCELLED':         return '🚫';
      case 'DELETED':           return '🗑️';
      case 'RENEWED':           return '🔄';
      case 'STATUS_CHANGED':    return '🔀';
      case 'DOCUMENT_UPLOADED': return '📎';
      case 'DOCUMENT_DELETED':  return '🗑️';
      case 'WHATSAPP_SENT':     return '📲';
      case 'EMAIL_SENT':        return '✉️';
      default:                  return '📌';
    }
  };

  const getActionLabel = (action: string): string => {
    switch (action) {
      case 'NOTE_ADDED':        return 'Added a note';
      case 'CREATED':           return 'Policy created';
      case 'EDITED':            return 'Policy updated';
      case 'CANCELLED':         return 'Policy cancelled';
      case 'DELETED':           return 'Policy deleted';
      case 'RENEWED':           return 'Policy renewed';
      case 'STATUS_CHANGED':    return 'Status changed';
      case 'DOCUMENT_UPLOADED': return 'Document uploaded';
      case 'DOCUMENT_DELETED':  return 'Document deleted';
      case 'WHATSAPP_SENT':     return 'WhatsApp sent';
      case 'EMAIL_SENT':        return 'Email sent';
      default:                  return action.replace(/_/g, ' ').toLowerCase();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-lg font-semibold text-gray-800">Activity Log</h4>
          <p className="text-sm text-gray-500 mt-1">
            {activityLogs.length} activity {activityLogs.length !== 1 ? 'entries' : 'entry'}
          </p>
        </div>
        <button
          onClick={() => setShowAddNote(!showAddNote)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Note
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
          <button
            onClick={() => setError('')}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {showAddNote && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <label className="text-sm font-medium text-gray-700">Add a note</label>
            <button
              onClick={() => {
                setShowAddNote(false);
                setNewNote('');
                setError('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your note here..."
          />
          <div className="flex justify-end space-x-3 mt-3">
            <button
              onClick={() => {
                setShowAddNote(false);
                setNewNote('');
                setError('');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddNote}
              disabled={isSubmitting || !newNote.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Add Note'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="animate-spin w-8 h-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <p className="text-gray-600">Loading activity log...</p>
        </div>
      ) : activityLogs.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <UserIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No activity yet</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Add a note to start tracking activity for this policy.
          </p>
        </div>
      ) : (
        <div className="flow-root">
          <ul className="-mb-8">
            {activityLogs.map((log, index) => (
              <li key={log.id}>
                <div className="relative pb-8">
                  {index < activityLogs.length - 1 && (
                    <span
                      className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                      aria-hidden="true"
                    />
                  )}
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white">
                        <span className="text-lg">{getActionIcon(log.action_type)}</span>
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {getActionLabel(log.action_type)}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">{log.description}</p>
                      <div className="flex items-center mt-2">
                        <UserIcon className="w-3 h-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">
                          {log.created_by || 'System'}
                        </span>
                        <span className="mx-2 text-gray-300">•</span>
                        <CalendarDaysIcon className="w-3 h-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">
                          {formatDateTime(log.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PolicyNotesTab;